/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  FlightRule,
  TransportOfferAlgorithm,
  TransportOfferOptionType,
  VehicleType,
} from 'src/graphql/generated/graphql.generated';
import {
  RequiredCapabilityRecord,
  RequiredCapabilityProps,
} from 'src/store/generated/required-capability-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TransportOfferOptionProps {
  id: string;
  requestedVehicleType: VehicleType | null;
  transportOfferAlgorithm: TransportOfferAlgorithm | null;
  minimumFlightRule: FlightRule | null;
  includeCompetitors: boolean | null;
  includeAbortedUnit: boolean;
  type: TransportOfferOptionType;
  requiredCapabilities: List<RequiredCapabilityProps>;
  requestId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TransportOfferOptionRecord extends Record<TransportOfferOptionProps>({
  id: null as any,
  requestedVehicleType: null as any,
  transportOfferAlgorithm: null as any,
  minimumFlightRule: null as any,
  includeCompetitors: null as any,
  includeAbortedUnit: null as any,
  type: null as any,
  requiredCapabilities: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    requestedVehicleType: VehicleType | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    includeCompetitors: boolean | null;
    includeAbortedUnit: boolean;
    type: TransportOfferOptionType;
    requiredCapabilities: any;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TransportOfferOptionRecord {
    if (json == null) {
      throw new Error('TransportOfferOptionRecord.fromJS: parameter cannot be null');
    }
    return new TransportOfferOptionRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TransportOfferOptionJson.id',
      ),
      requestedVehicleType: json.requestedVehicleType,
      transportOfferAlgorithm: json.transportOfferAlgorithm,
      minimumFlightRule: json.minimumFlightRule,
      includeCompetitors: json.includeCompetitors,
      includeAbortedUnit: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'includeAbortedUnit', false),
        'TransportOfferOptionJson.includeAbortedUnit',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', TransportOfferOptionType.User),
        'TransportOfferOptionJson.type',
      ),
      requiredCapabilities: List<RequiredCapabilityRecord>(
        getFromOrThrow(
          json,
          (j) => j.requiredCapabilities,
          'TransportOfferOptionJson.requiredCapabilities',
        ).map((o: any) => new RequiredCapabilityRecord(o)),
      ),
      requestId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
        'TransportOfferOptionJson.requestId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TransportOfferOptionJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    requestedVehicleType: VehicleType | null;
    transportOfferAlgorithm: TransportOfferAlgorithm | null;
    minimumFlightRule: FlightRule | null;
    includeCompetitors: boolean | null;
    includeAbortedUnit: boolean;
    type: TransportOfferOptionType;
    requiredCapabilities: any;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TransportOfferOptionRecord, Error[]] {
    if (json == null) {
      throw new Error('TransportOfferOptionRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TransportOfferOptionRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TransportOfferOptionJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedVehicleType: tryOrDefault(
        () => json.requestedVehicleType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportOfferAlgorithm: tryOrDefault(
        () => json.transportOfferAlgorithm,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      minimumFlightRule: tryOrDefault(
        () => json.minimumFlightRule,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeCompetitors: tryOrDefault(
        () => json.includeCompetitors,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      includeAbortedUnit: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'includeAbortedUnit', false),
            'TransportOfferOptionJson.includeAbortedUnit',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', TransportOfferOptionType.User),
            'TransportOfferOptionJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiredCapabilities: tryOrDefault(
        () =>
          List<RequiredCapabilityRecord>(
            getFromOrThrow(
              json,
              (j) => j.requiredCapabilities,
              'TransportOfferOptionJson.requiredCapabilities',
            ).map((o: any) => new RequiredCapabilityRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
            'TransportOfferOptionJson.requestId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TransportOfferOptionJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TransportOfferOptionProps) {
    super(props);
    if (props == null) {
      throw new Error('TransportOfferOptionRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TransportOfferOptionViewModelBase implements TransportOfferOptionProps {
  protected readonly _props: TransportOfferOptionProps;
  private _json?: TransportOfferOptionProps;

  get id(): string {
    return this._props.id;
  }
  get requestedVehicleType(): VehicleType | null {
    return this._props.requestedVehicleType;
  }
  get transportOfferAlgorithm(): TransportOfferAlgorithm | null {
    return this._props.transportOfferAlgorithm;
  }
  get minimumFlightRule(): FlightRule | null {
    return this._props.minimumFlightRule;
  }
  get includeCompetitors(): boolean | null {
    return this._props.includeCompetitors;
  }
  get includeAbortedUnit(): boolean {
    return this._props.includeAbortedUnit;
  }
  get type(): TransportOfferOptionType {
    return this._props.type;
  }
  get requiredCapabilities(): List<RequiredCapabilityProps> {
    return this._props.requiredCapabilities;
  }
  get requestId(): string {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAir() {
    return this._props.requestedVehicleType === VehicleType.Air;
  }
  public get isFixedWing() {
    return this._props.requestedVehicleType === VehicleType.FixedWing;
  }
  public get isGround() {
    return this._props.requestedVehicleType === VehicleType.Ground;
  }
  public get isRotorWing() {
    return this._props.requestedVehicleType === VehicleType.RotorWing;
  }
  public get isUnspecified() {
    return this._props.requestedVehicleType === VehicleType.Unspecified;
  }
  public get isShortestDistance() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestDistance;
  }
  public get isShortestEta() {
    return this._props.transportOfferAlgorithm === TransportOfferAlgorithm.ShortestEta;
  }
  public get isIfr() {
    return this._props.minimumFlightRule === FlightRule.Ifr;
  }
  public get isNone() {
    return this._props.minimumFlightRule === FlightRule.None;
  }
  public get isVfr() {
    return this._props.minimumFlightRule === FlightRule.Vfr;
  }
  public get isApplied() {
    return this._props.type === TransportOfferOptionType.Applied;
  }
  public get isUser() {
    return this._props.type === TransportOfferOptionType.User;
  }

  public isAnyRequestedVehicleType(...vehicleType: VehicleType[]) {
    return (
      this._props.requestedVehicleType != null &&
      vehicleType.includes(this._props.requestedVehicleType)
    );
  }
  public isAnyTransportOfferAlgorithm(...transportOfferAlgorithm: TransportOfferAlgorithm[]) {
    return (
      this._props.transportOfferAlgorithm != null &&
      transportOfferAlgorithm.includes(this._props.transportOfferAlgorithm)
    );
  }
  public isAnyMinimumFlightRule(...flightRule: FlightRule[]) {
    return (
      this._props.minimumFlightRule != null && flightRule.includes(this._props.minimumFlightRule)
    );
  }
  public isAnyType(...transportOfferOptionType: TransportOfferOptionType[]) {
    return transportOfferOptionType.includes(this._props.type);
  }

  constructor(props: TransportOfferOptionProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        requestedVehicleType: this._props.requestedVehicleType,
        transportOfferAlgorithm: this._props.transportOfferAlgorithm,
        minimumFlightRule: this._props.minimumFlightRule,
        includeCompetitors: this._props.includeCompetitors,
        includeAbortedUnit: this._props.includeAbortedUnit,
        type: this._props.type,
        requiredCapabilities: this._props.requiredCapabilities,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTransportOfferOptionEntities = (state: AppState) =>
  state.entityStores.transportOfferOption.entities;

export const selectTransportOfferOptionById = (id: string) => (state: AppState) =>
  state.entityStores.transportOfferOption.entities.get(id);

export const selectTransportOfferOptionMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.transportOfferOption.meta.get(id);

export const selectTransportOfferOptionAndMetadataById = (id: string) =>
  createSelector(
    selectTransportOfferOptionById(id),
    selectTransportOfferOptionMetadataById(id),
    (transportOfferOption, meta) => ({ transportOfferOption, meta }),
  );

export function selectTransportOfferOptionEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTransportOfferOptionEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TransportOfferOptionRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTransportOfferOptionEntitiesByRequestedVehicleType(
  requestedVehicleType: VehicleType,
) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.requestedVehicleType === requestedVehicleType).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByTransportOfferAlgorithm(
  transportOfferAlgorithm: TransportOfferAlgorithm,
) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.transportOfferAlgorithm === transportOfferAlgorithm).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByMinimumFlightRule(
  minimumFlightRule: FlightRule,
) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.minimumFlightRule === minimumFlightRule).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByIncludeCompetitors(
  includeCompetitors: boolean,
) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.includeCompetitors === includeCompetitors).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByIncludeAbortedUnit(
  includeAbortedUnit: boolean,
) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.includeAbortedUnit === includeAbortedUnit).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByType(type: TransportOfferOptionType) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectTransportOfferOptionEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTransportOfferOptionEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
